import React, { useState, useMemo } from 'react';
import './App.css';
import FilterControls from './components/FilterControls';
import MeetingList from './components/MeetingList';
import CalendarView from './components/CalendarView';
import ViewToggle from './components/ViewToggle';
import { getAllMeetings } from './meetingData';

function App() {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [filters, setFilters] = useState({
    types: [],
    city: '',
    lodge: '',
    dresscode: '',
    search: '',
    storloge: false,
    igld: false,
    showSamain: true,
    bardaland: true,
    jarnbaraland: false,
    folkungaland: false
  });

  const allMeetings = getAllMeetings();

  const filteredMeetings = useMemo(() => {
    return allMeetings.filter(meeting => {
      // Samain override logic - Samain meetings always show unless explicitly hidden
      const isSamainMeeting = meeting.lodge === 'Samain';
      if (isSamainMeeting && filters.showSamain) {
        return true; // Samain meetings bypass all other filters when showSamain is true
      }
      if (isSamainMeeting && !filters.showSamain) {
        return false; // Hide Samain meetings if user unchecked showSamain
      }

      // Storloge override logic - Storloge meetings always show when filter is active
      if (filters.storloge) {
        const isStorlogeMeeting = meeting.lodge?.includes('Storloge');
        if (isStorlogeMeeting) {
          return true; // Storloge meetings bypass all other filters when storloge filter is active
        }
      }

      // IGLD override logic - IGLD meetings always show when filter is active
      if (filters.igld) {
        const isIgldMeeting = meeting.lodge?.includes('RiksStorLogen') || 
                             meeting.details?.includes('IGLD');
        if (isIgldMeeting) {
          return true; // IGLD meetings bypass all other filters when igld filter is active
        }
      }

      // Filter by meeting types
      if (filters.types.length > 0) {
        const meetingTypes = [meeting.type, meeting.meeting].join(' ').toLowerCase();
        const hasMatchingType = filters.types.some(filterType => 
          meetingTypes.includes(filterType.toLowerCase()) ||
          (filterType === 'Eu' && meetingTypes.includes('eu')) ||
          (filterType === 'D' && meetingTypes.includes('d')) ||
          (filterType === 'B' && meetingTypes.includes('b'))
        );
        if (!hasMatchingType) return false;
      }

      // Filter by city
      if (filters.city && meeting.city !== filters.city) {
        return false;
      }

      // Filter by lodge
      if (filters.lodge && meeting.lodge !== filters.lodge) {
        return false;
      }

      // Filter by dress code
      if (filters.dresscode) {
        if (filters.dresscode === 'Frack' && meeting.dresscode !== 'Frack') {
          return false;
        }
        if (filters.dresscode === 'Vårdad' && meeting.dresscode !== 'Vårdad') {
          return false;
        }
        if (filters.dresscode === 'Casual' && 
            meeting.dresscode !== '-' && 
            meeting.dresscode !== '' && 
            meeting.dresscode !== 'Frack' && 
            meeting.dresscode !== 'Vårdad') {
          return false;
        }
      }

      // Filter by search text in details
      if (filters.search) {
        const searchText = filters.search.toLowerCase();
        const searchableText = [
          meeting.details,
          meeting.place,
          meeting.lodge,
          meeting.city
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(searchText)) {
          return false;
        }
      }

      // Regional meeting logic with OR logic (additive)
      const isBardalandMeeting = !meeting.region; // Bardaland meetings have no region field
      const isJarnbaralandMeeting = meeting.region === 'Järnbäraland';
      const isFolkungalandMeeting = meeting.region === 'Folkungaland';
      
      // Check if a specific regional lodge is selected
      const selectedBardalandLodge = filters.lodge && isBardalandMeeting && meeting.lodge === filters.lodge;
      const selectedJarnbaralandLodge = filters.lodge && isJarnbaralandMeeting && meeting.lodge === filters.lodge;
      const selectedFolkungalandLodge = filters.lodge && isFolkungalandMeeting && meeting.lodge === filters.lodge;
      
      // Check which regional filters are active
      const activeRegionalFilters = [filters.bardaland, filters.jarnbaraland, filters.folkungaland];
      const hasActiveRegionalFilter = activeRegionalFilters.some(filter => filter);
      
      // If any regional filter is active, show meeting if it matches ANY of the active regions (OR logic)
      if (hasActiveRegionalFilter) {
        const isInActiveRegion = (filters.bardaland && isBardalandMeeting) ||
                                (filters.jarnbaraland && isJarnbaralandMeeting) ||
                                (filters.folkungaland && isFolkungalandMeeting);
        
        // Also show if specific lodge is selected from any region
        const isSelectedLodge = selectedBardalandLodge || selectedJarnbaralandLodge || selectedFolkungalandLodge;
        
        if (!isInActiveRegion && !isSelectedLodge) {
          return false;
        }
      }

      return true;
    });
  }, [allMeetings, filters]);

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="header-text">
            <h1>Bardalands Logemöten Hösten 2025</h1>
            <p>Översikt och filter för alla logemöten under hösten</p>
          </div>
          <ViewToggle 
            viewMode={viewMode} 
            onViewChange={setViewMode}
          />
        </div>
      </header>
      
      <div className="app-layout">
        <aside className="sidebar">
          <FilterControls 
            filters={filters} 
            onFilterChange={setFilters}
            meetingCount={filteredMeetings.length}
          />
        </aside>
        
        <main className="main-content">
          {viewMode === 'list' ? (
            <MeetingList meetings={filteredMeetings} />
          ) : (
            <CalendarView meetings={filteredMeetings} />
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
